
import {Vue, Component, Mixins} from 'vue-property-decorator';
import TeacherNotice from 'src/modules/teacher/TeacherNotice.vue';
import {openSubscribePage} from 'src/utils/NavigationUtils';
import {analyticsController} from 'src/modules/user/analytics/AnalyticsController';
import {getAssetItem} from 'src/utils/helpers';
import {type StripePrice, subscriptionService} from '../../user/subscription/SubscriptionService';
import {currentPromotion} from '../marketing/PromotionController';
import {userService} from '../../user/UserService';
import {SwipeToDismissMixin} from '../mixins/SwipeToDismissMixin';

@Component({
  components: { TeacherNotice },
  methods: { getAssetItem },
})
export default class FreemiumSubscriptionPopup extends Mixins(SwipeToDismissMixin) {
  private isDisplayed: boolean = false;
  private showBenefitsPopup: boolean = false;
  private promotion: number = 50;
  private isLoading: boolean = false;
  private slide: number = 1;
  private importMusicxmlPieceQuota: boolean = false;

  benefits = [
    { icon: 'mdi-infinity',   key: 'unlimitedAccess' },
    { icon: 'mdi-robot',      key: 'virtualTeacher' },
    { icon: 'mdi-chart-line', key: 'progressTracking' },
    { icon: 'mdi-file',       key: 'importPieces' },
  ];

  testimonials = [
    { name: 1, id: '1' }, { name: 2, id: '2' }, { name: 3, id: '3' },
    { name: 4, id: '4' }, { name: 5, id: '5' }, { name: 6, id: '6' },
    { name: 7, id: '7' }, { name: 8, id: '8' }, { name: 9, id: '9' },
  ];

  protected dismissSheet() {
    this.isDisplayed = false;
  }

  mounted() {
    this.$root.$on('displayFreemiumPopup', (importMusicxmlPieceQuota = false) => {
      this.isDisplayed = true;
      this.importMusicxmlPieceQuota = importMusicxmlPieceQuota;
      analyticsController.logBasicAnalyticsEvent('freemium popup displayed', 'Freemium');
    });
  }

  get monthlyPrice() { return subscriptionService.monthlyPrice; }
  get monthlyPriceHumanReadable() { return subscriptionService.humanizeUnitAmount(this.monthlyPrice); }
  get monthlyReductedPriceHumanReadable() {
    const reducedPrice: StripePrice = {
      ...this.monthlyPrice,
      unit_amount: this.monthlyPrice?.unit_amount * (1 - this.promotion / 100),
    };
    return subscriptionService.humanizeUnitAmount(reducedPrice);
  }
  get mainTitle() {
    return this.importMusicxmlPieceQuota
      ? this.$t('freemiumSubscriptionPopup.quotaReached')
      : this.$t('freemiumSubscriptionPopup.freeContentExhausted');
  }
  get mainSubtitle() {
    return this.importMusicxmlPieceQuota
      ? this.$t('freemiumSubscriptionPopup.importUnlimited')
      : this.$t('freemiumSubscriptionPopup.unlockToday');
  }
  get isLandscape() { return this.$q.screen.width > this.$q.screen.height; }
  get showAsActionPopup() { return this.$q.screen.gt.sm || this.$q.screen.width < this.$q.screen.height; }
  get isUserIsInBPaymentGroup() { return userService.user.paymentGroupTesting === 'B'; }

  get nextSundayDate(): string {
    const now = new Date();
    const nextSunday = new Date(now);
    const daysUntilMonday = (8 - now.getDay()) % 7;
    nextSunday.setDate(now.getDate() + (daysUntilMonday === 0 ? 7 : daysUntilMonday));
    nextSunday.setHours(0, 0, 0, 0);
    const formatted = nextSunday.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  get tomorrowDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const formatted = tomorrow.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  get translatedBenefits() {
    return this.benefits.map(b => ({
      ...b,
      title: this.$t(`freemiumSubscriptionPopup.benefits.${b.key}Title`) as string || '',
      text: this.$t(`freemiumSubscriptionPopup.benefits.${b.key}`) as string,
    }));
  }
  get translatedTestimonials() {
    return this.testimonials.map(t => ({
      ...t,
      text: this.$t(`freemiumSubscriptionPopup.testimonials.${t.id}.text`) as string,
      author: this.$t(`freemiumSubscriptionPopup.testimonials.${t.id}.author`) as string,
    }));
  }

  showBenefits() {
    this.showBenefitsPopup = true;
    analyticsController.logBasicAnalyticsEvent('freemium popup benefits shown', 'Freemium');
  }
  closeBenefitsPopup() {
    this.showBenefitsPopup = false;
    analyticsController.logBasicAnalyticsEvent('freemium popup benefits closed', 'Freemium');
  }
  closePopup() {
    analyticsController.logBasicAnalyticsEvent('freemium popup closed', 'Freemium');
    this.isDisplayed = false;
  }
  goToSubscribe() {
    analyticsController.logBasicAnalyticsEvent('freemium popup subscribe clicked', 'Freemium');
    this.isDisplayed = false;
    openSubscribePage.bind(this)(this.$route.path, 'freemiumPopup', undefined, undefined, true);
  }
  subscribe() {
    this.isLoading = true;
    analyticsController.logBasicAnalyticsEvent('freemium popup subscribe for month clicked', 'Freemium');
    subscriptionService
      .redirectToCheckout(subscriptionService.monthlyPrice, currentPromotion, 'subscribe', true)
      .then((val) => { if (val === 'ios_purchase_success') this.$router.push('/subscribe/success'); })
      .finally(() => { this.isLoading = false; });
  }
}
